// File: src/components/pages/StudentDashboard.jsx
// Upcoming booking view for students (ServiceUser role)

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  LayoutDashboard,
  CalendarDays,
  MapPin,
  Clock,
  RefreshCw,
  Calendar,
  GraduationCap,
  Car
} from 'lucide-react';
import { bookingAPI } from '../../services/api';
import '../../styles/unified.css';

// ─── helpers ──────────────────────────────────────────────────────────────────


const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${m} ${ampm}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, mo, d] = dateStr.split('-');
  return new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)))
    .toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const [y, mo, d] = dateStr.split('-');
  return new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)))
    .toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

const statusColour = (status) => {
  switch (status) {
    case 'confirmed': return { bg: '#dcfce7', text: '#16a34a', label: 'Confirmed' };
    case 'pending':   return { bg: '#fef9c3', text: '#ca8a04', label: 'Pending'   };
    case 'cancelled': return { bg: '#fee2e2', text: '#dc2626', label: 'Cancelled' };
    default:          return { bg: '#f1f5f9', text: '#64748b', label: status      };
  }
};

// ─── Instructor BookingCard ──────────────────────────────────────────────────


const UpcomingBookingCard = ({ booking, isNext }) => {
  const [hovered, setHovered] = useState(false);
  const s = statusColour(booking.status);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        border: `1px solid ${hovered ? '#667eea' : isNext ? '#667eea40' : '#e5e7eb'}`,
        borderRadius: '0.75rem',
        padding: '1.1rem 1.4rem',
        display: 'flex',
        gap: '1.1rem',
        alignItems: 'flex-start',
        boxShadow: isNext
          ? '0 2px 12px rgba(102,126,234,0.14)'
          : hovered ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        position: 'relative'
      }}
    >
      {isNext && (
        <span style={{
          position: 'absolute', top: '-10px', left: '1rem',
          background: 'linear-gradient(135deg,#667eea,#764ba2)',
          color: '#fff', fontSize: '0.65rem', fontWeight: 700,
          padding: '0.15rem 0.55rem', borderRadius: '9999px',
          letterSpacing: '0.04em', textTransform: 'uppercase'
        }}>
          Next lesson
        </span>
      )}

      {/* Date + time pill */}
      <div style={{ minWidth: '80px', textAlign: 'center' }}>
        <div style={{
          background: isNext
            ? 'linear-gradient(135deg,#667eea,#764ba2)'
            : 'linear-gradient(135deg,#94a3b8,#64748b)',
          borderRadius: '0.6rem',
          padding: '0.55rem 0.4rem'
        }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.15rem', lineHeight: 1 }}>
            {booking.date ? booking.date.split('-')[2] : ''}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.68rem', fontWeight: 600, marginTop: '0.1rem' }}>
            {booking.date ? formatDateShort(booking.date).split(' ').slice(1).join(' ') : ''}
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.25)', margin: '0.35rem 0 0.25rem' }} />
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem' }}>
            {formatTime(booking.startTime)}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.63rem' }}>
            {formatTime(booking.endTime)}
          </div>
        </div>
      </div>

      {/* Details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.45rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <Car size={14} color="#667eea" />
            <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#111827' }}>Driving Lesson</span>
          </div>
          <span style={{
            background: s.bg, color: s.text,
            fontSize: '0.68rem', fontWeight: 700,
            padding: '0.12rem 0.5rem', borderRadius: '9999px',
            textTransform: 'capitalize'
          }}>
            {s.label}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.4rem' }}>
          <Calendar size={13} color="#6b7280" />
          <span style={{ fontSize: '0.82rem', color: '#374151' }}>
            {formatDate(booking.date)}
          </span>
        </div>

        {(booking.pickupAddress || booking.pickupPostcode) ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.35rem' }}>
            <MapPin size={13} color="#667eea" style={{ marginTop: '2px', flexShrink: 0 }} />
            <span style={{ fontSize: '0.82rem', color: '#4b5563' }}>
              {[booking.pickupAddress, booking.pickupPostcode].filter(Boolean).join(', ')}
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <MapPin size={13} color="#d1d5db" />
            <span style={{ fontSize: '0.78rem', color: '#9ca3af', fontStyle: 'italic' }}>
              No pickup address on file
            </span>
          </div>
        )}

        {booking.instructorName && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.35rem' }}>
            <GraduationCap size={13} color="#6b7280" />
            <span style={{ fontSize: '0.82rem', color: '#374151' }}>{booking.instructorName}</span>
          </div>
        )}
      </div>
    </div>
  );
};


// ─── StudentDashboard ─────────────────────────────────────────────────────────

const StudentDashboard = ({ user, getAccessTokenSilently }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await bookingAPI.getMyBookings(getAccessTokenSilently);
      if (res.success) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcoming = (res.data || [])
          .filter(b => new Date(b.date) >= today)
          .sort((a, b) => {
            const dc = new Date(a.date) - new Date(b.date);
            return dc !== 0 ? dc : a.startTime.localeCompare(b.startTime);
          });
        setBookings(upcoming);
      } else {
        setError('Failed to load your bookings.');
      }
    } catch (err) {
      console.error('Student dashboard fetch error:', err);
      setError(err.message || 'Failed to load your bookings.');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1.75rem', paddingBottom: '1rem',
        borderBottom: '2px solid #e5e7eb', flexWrap: 'wrap', gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <LayoutDashboard size={28} color="#667eea" />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 700, color: '#111827' }}>Dashboard</h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
              Welcome back, {user?.name?.split(' ')[0] || 'there'}
            </p>
          </div>
        </div>
        <button
          onClick={fetchBookings}
          disabled={loading}
          title="Refresh"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            background: '#fff', border: '1px solid #e5e7eb',
            borderRadius: '0.5rem', padding: '0.45rem 0.85rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            color: '#6b7280', fontSize: '0.85rem', fontWeight: 500,
            transition: 'all 0.15s', opacity: loading ? 0.6 : 1
          }}
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Section heading */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        marginBottom: '1.25rem', paddingBottom: '0.6rem',
        borderBottom: '2px solid #667eea25'
      }}>
        <CalendarDays size={18} color="#667eea" />
        <div>
          <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#111827' }}>Upcoming Lessons</h2>
          <p style={{ margin: 0, fontSize: '0.78rem', color: '#6b7280' }}>Your scheduled driving lessons</p>
        </div>
        {!loading && !error && (
          <span style={{
            marginLeft: 'auto',
            background: '#667eea15', color: '#667eea',
            fontWeight: 700, fontSize: '0.78rem',
            padding: '0.2rem 0.65rem', borderRadius: '9999px'
          }}>
            {bookings.length} {bookings.length === 1 ? 'lesson' : 'lessons'}
          </span>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <div className="spinner" style={{ margin: '0 auto 1rem' }} />
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Loading your bookings…</p>
        </div>
      )}

      {!loading && error && (
        <div style={{
          background: '#fee2e2', border: '1px solid #fca5a5',
          borderRadius: '0.75rem', padding: '1.25rem 1.5rem',
          color: '#dc2626', display: 'flex', alignItems: 'center',
          gap: '0.75rem', flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 600 }}>Error:</span>
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={fetchBookings} style={{
            background: '#dc2626', color: '#fff', border: 'none',
            borderRadius: '0.4rem', padding: '0.35rem 0.85rem',
            cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
          }}>Retry</button>
        </div>
      )}

      {!loading && !error && bookings.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '3.5rem 2rem',
          background: '#f8fafc', borderRadius: '0.75rem',
          border: '1px dashed #e2e8f0'
        }}>
          <CalendarDays size={40} color="#d1d5db" style={{ marginBottom: '0.75rem' }} />
          <h3 style={{ margin: '0 0 0.35rem', color: '#374151', fontSize: '1rem', fontWeight: 600 }}>
            No upcoming lessons
          </h3>
          <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.88rem' }}>
            Your next lessons will appear here once booked.
          </p>
        </div>
      )}

      {!loading && !error && bookings.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {bookings.map((b, i) => (
            <UpcomingBookingCard key={b.bookingId} booking={b} isNext={i === 0} />
          ))}
        </div>
      )}
    </div>
  );
};


export default StudentDashboard;
